<script lang="ts">
  import { type Snippet, setContext } from 'svelte';
  import { onMount } from 'svelte';

  import SortDirection from '$provider/lib/apis/models/SortDirection';
  import DataTableHeader from './DataTableHeader.svelte';
  import { MinColWidth } from '$shared/constants/DataTableConstants';
  import DataTableColumn from './DataTableColumn.svelte';
  import DataTableLoader from './DataTableLoader.svelte';
  import ContextMenu from '$shared/atoms/ContextMenu/ContextMenu.svelte';
  import Spinner from '$shared/atoms/Spinner/Spinner.svelte';
  import Checkbox from '$shared/atoms/Checkbox/Checkbox.svelte';
  import OptionCell from './OptionCell.svelte';
  import SelectableHeaderAction from './SelectableHeaderAction.svelte';
  import { getContextMenuList } from '$provider/constants/ContextMenu';
  import { DataTableRowColor } from './DataTableProp';
  import CheckboxCell from './CheckboxCell.svelte';

  const MapClassNameWithRowColor = {
    [DataTableRowColor.Default]: 'color-default',
    [DataTableRowColor.Warn]: 'color-warn',
    [DataTableRowColor.Active]: 'color-active'
  };

  const HoverKey = 'hover';
  export const DataTableHoverContextKey = '__dt_hover__';

  const LoaderRowCount = 3;

  interface Props {
    loading?: boolean;
    columns?: ColumnType[];
    rows?: any[];
    selectedRows?: Set<string>;
    disabledRows?: Set<string>;
    hasOptions?: boolean;
    hideContextMenu?: boolean;
    hasSelectableRow?: boolean;
    haveSelectAll?: boolean;
    showSelectableAction?: boolean;
    getRowOptions?: any;
    name?: string;
    overflow?: boolean;
    multiSorting?: boolean;
    rowClickActionDisabled?: boolean;
    disableUnselectedCheckbox?: boolean;
    selectAllChecked?: boolean;
    customCheckboxComponent?: ConstructorOfATypedSvelteComponent | undefined;
    selectableAction?: Snippet | undefined;
    children?: Snippet | undefined;
    onsort?: (payload: { column: ColumnType; sortDirection: SortDirection }) => void;
    onclick?: (payload: any) => void;
    onchecked?: (payload: any) => void;
    oncontextmenu?: (payload: { option: any; row: any }) => void;
    onclickoption?: (payload: { option: CommonList; row: any }) => void;
    onselectall?: (payload: boolean) => void;
    onevent?: (payload: any) => void;
  }

  let {
    loading = false,
    columns = [],
    rows = $bindable([]),
    selectedRows = new Set(),
    disabledRows = new Set(),
    hasOptions = false,
    hideContextMenu = false,
    hasSelectableRow = false,
    haveSelectAll = false,
    showSelectableAction = false,
    getRowOptions = () => {},
    name = '',
    overflow = false,
    multiSorting = false,
    rowClickActionDisabled = false,
    disableUnselectedCheckbox = false,
    selectAllChecked = $bindable(false),
    customCheckboxComponent = undefined,
    selectableAction = undefined,
    children = undefined,
    onsort,
    onclick,
    onchecked,
    oncontextmenu,
    onclickoption,
    onselectall,
    onevent
  }: Props = $props();

  export const resetSort = () => {
    activeSortColumn = '';
    resetSortDirection();
  };

  const DefaultSortDirection: SortDirection = SortDirection.Asc;
  const contextMenu = getContextMenuList();
  let parent: HTMLDivElement;
  let columnSortDirectionMap = $state({});
  let tableWidth = $derived(
    columns.length !== 0
      ? columns.map((col) => col.width || MinColWidth).reduce((total = 0, width) => total + width) +
          (hasOptions ? 30 : 0) +
          (hasSelectableRow ? 30 : 0)
      : '100%'
  );
  let activeSortColumn = $state('');
  let contextMenuPos = $state({ x: 0, y: 0 });
  let hoveredRow = $state<any>(null);
  let showContextMenu = $state(false);
  let selectedContextMenuRow = undefined;
  let colLength = $derived(columns.length + [hasOptions, hasSelectableRow].filter(Boolean).length);
  let hasRowSelected = $derived(rows?.some((row) => selectedRows.has(row.id)));
  let displaySelectableAction = $derived(
    showSelectableAction && hasSelectableRow && selectedRows.size > 0 && hasRowSelected
  );

  onMount(() => {
    setInitialSortDirection();
  });

  setContext(DataTableHoverContextKey, {
    get current() {
      return hoveredRow;
    }
  });

  const setInitialSortDirection = (): void => {
    columns.forEach((col) => {
      columnSortDirectionMap[col.id] = col.defaultSortDirection || DefaultSortDirection;
    });
  };

  const resetSortDirection = (): void => {
    columns.forEach((col) => {
      columnSortDirectionMap[col.id] = DefaultSortDirection;
    });
  };

  const getReveresedSortDirection = (sortDirection: SortDirection): SortDirection => {
    if (sortDirection === SortDirection.Asc) {
      return SortDirection.Desc;
    }
    return SortDirection.Asc;
  };

  const onClickSort = (column: ColumnType): void => {
    const tempActiveSortDirection = columnSortDirectionMap[column.id];
    !multiSorting && resetSortDirection();

    if (column.id !== activeSortColumn) {
      activeSortColumn = column.id;
    }

    columnSortDirectionMap[column.id] = getReveresedSortDirection(tempActiveSortDirection);
    columnSortDirectionMap = { ...columnSortDirectionMap };
    onsort?.({ column, sortDirection: columnSortDirectionMap[column.id] });
  };

  const onClickRow = (row): void => {
    if (rowClickActionDisabled) return;
    onclick?.(row);
  };

  const onClickCheckbox = (row): void => {
    onchecked?.(row);
  };

  const onClickContextMenu = (e, row) => {
    showContextMenu = true;
    selectedContextMenuRow = row;
    contextMenuPos = { x: e.clientX, y: e.clientY };
  };

  const onClickContextMenuList = (detail: any) => {
    showContextMenu = false;
    oncontextmenu?.({ option: detail, row: selectedContextMenuRow });
    selectedContextMenuRow = undefined;
  };

  const onClickWindow = (e): void => {
    if (parent && !parent.contains(e.target)) {
      showContextMenu = false;
    }
  };

  const onClickOption = (option: CommonList, row: any): void => {
    onclickoption?.({ option, row });
  };

  const onClickHeaderCheckbox = (e: any): void => {
    onselectall?.(e.target?.checked);
    selectAllChecked = e.target?.checked;
  };

  const getColorClassName = (rowColor: DataTableRowColor): string => {
    return MapClassNameWithRowColor[rowColor] || '';
  };

  const onMouseEnter = (row: any): void => {
    if (!(HoverKey in row)) return;
    hoveredRow = row;
  };

  const onMouseLeave = (row: any): void => {
    if (!(HoverKey in row)) return;
    hoveredRow = null;
  };
</script>

<svelte:window onmouseup={onClickWindow} />

{#if displaySelectableAction}
  <SelectableHeaderAction selectedCount={selectedRows?.size} onchange={onClickHeaderCheckbox}>
    {@render selectableAction?.()}
  </SelectableHeaderAction>
{/if}
<div bind:this={parent} class="comp-data-table">
  <table data-cy={`${name}-table`} width={`${tableWidth}px`} cellspacing="1">
    {#if !displaySelectableAction}
      <thead data-cy={`${name}-thead`}>
        {#if hasSelectableRow}
          <th class="option-head" width="30px">
            {#if haveSelectAll && rows.length > 0}
              <div data-cy="table-checkbox" class="checkbox">
                <Checkbox
                  name="select-all"
                  checked={selectAllChecked}
                  onchange={onClickHeaderCheckbox}
                />
              </div>
            {/if}
          </th>
        {/if}

        {#each columns as col}
          <DataTableHeader
            activeSortColumn={activeSortColumn === col.id}
            sortDirection={columnSortDirectionMap[col.id]}
            {col}
            onsort={() => {
              onClickSort(col);
            }}
          />
        {/each}
        {#if hasOptions}
          <th class="option-head" width="30px"></th>
        {/if}
      </thead>
    {:else}
      <colgroup>
        {#if hasSelectableRow}
          <col width={`30px`} />
        {/if}
        {#each columns || [] as column}
          <col width={`${column.width}px`} />
        {/each}
      </colgroup>
    {/if}
    <tbody data-cy={`${name}-tbody`}>
      {#if loading}
        <DataTableLoader columnLength={colLength} count={LoaderRowCount} />
      {:else}
        {#each rows as row}
          <tr
            class:highlight={row.highlight}
            class={getColorClassName(row.color)}
            data-cy="table-row"
            class:click-disabled={rowClickActionDisabled}
            onclick={(e) => {
              e.preventDefault();
              onClickRow(row);
            }}
            onmouseenter={() => onMouseEnter(row)}
            onmouseleave={() => onMouseLeave(row)}
            oncontextmenu={(e) => {
              if (!hideContextMenu) {
                e.preventDefault();
                onClickContextMenu(e, row);
              }
            }}
          >
            {#if hasSelectableRow}
              <td class="options" width="30px">
                <div data-cy="table-checkbox" class="data-checkbox">
                  {#if customCheckboxComponent}
                    {@const SvelteComponent = customCheckboxComponent}
                    <SvelteComponent {row} {onevent} />
                  {:else}
                    <CheckboxCell
                      name={row.id}
                      checked={selectedRows.has(row?.id)}
                      disabled={disabledRows.has(row?.id) ||
                        (disableUnselectedCheckbox && !selectedRows.has(row?.id))}
                      onchange={() => onClickCheckbox(row)}
                    />
                  {/if}
                </div>
              </td>
            {/if}
            {#each columns as col}
              <DataTableColumn {row} {col} {onevent} />
            {/each}
            {#if hasOptions}
              <td class="options" width="30px">
                {#if row.isLoading}
                  <div class="spinner-cell">
                    <Spinner />
                  </div>
                {:else}
                  <OptionCell
                    hasOverflow={overflow}
                    list={getRowOptions(row)}
                    onclick={(option) => {
                      onClickOption(option, row);
                    }}
                  />
                {/if}
              </td>
            {/if}
          </tr>
        {:else}
          <tr>
            <td class="empty-col" colspan={colLength}>No data found</td>
          </tr>
        {/each}
      {/if}
    </tbody>
  </table>
  {@render children?.()}
  {#if showContextMenu}
    <ContextMenu {...contextMenuPos} list={contextMenu} onclick={onClickContextMenuList} />
  {/if}
</div>

<style>
  .comp-data-table {
    width: 100%;
    max-height: 100%;
  }

  table {
    table-layout: fixed;
    min-width: 100%;
  }

  .empty-col {
    height: 36px;
    text-align: left;
    color: var(--grey-medium);
    font-style: italic;
    background-color: var(--grey-bg);
    padding: 0 8px;
  }

  table tbody tr {
    cursor: pointer;
  }

  table tbody tr:hover {
    background-color: var(--grey-bg);
  }
  table tbody tr.highlight {
    background-color: var(--red-light);
  }

  table tbody tr.color-warn {
    background-color: var(--yellow-light);
  }
  table tbody tr.color-active {
    background-color: var(--light-blue);
  }
  table tbody tr.click-disabled {
    cursor: not-allowed;
  }

  .option-head {
    width: 30px;
    height: 50px;
    line-height: 34px;
    padding: 0 8px 0px;
    border-bottom: 1px solid #e2e4e8;
  }

  .options {
    border-bottom: 1px solid #e2e4e8;
    vertical-align: middle;
  }

  .checkbox,
  .data-checkbox {
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .data-checkbox {
    margin-left: 8px;
  }
</style>
